import type { LevelTableColumn } from 'redesign';
import SortIcon from 'assets/img/icons/caret-down.svg?react';

import { Box, Button, styled, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { ContextMenuButton, LevelTableHeaderProps } from 'redesign';
import { TFunctionNonStrict } from 'i18next';
import { translateState } from './accreditations.types';
import {
  AccreditationCellProp,
  AccreditationsColumnDefinition,
  AccreditationTableDefinition,
} from './accreditations-table-provider.types';

export const getAccreditationsColumnDefinition = (
  t: TFunctionNonStrict<'hq-screen-specific', undefined>
): LevelTableColumn<AccreditationsColumnDefinition, AccreditationTableDefinition>[] => {
  const flexWorkerName: LevelTableColumn<
    AccreditationsColumnDefinition,
    AccreditationTableDefinition
  > = {
    Header: () => (
      <Header>
        {t('certificates.table_headers.flexworker_name', { ns: 'hq-screen-specific' })}
      </Header>
    ),
    accessor: 'flexWorkerName',
    Cell: FlexWorkerNameCell,
  };
  const certificate: LevelTableColumn<
    AccreditationsColumnDefinition,
    AccreditationTableDefinition
  > = {
    Header: () => (
      <Header>{t('certificates.table_headers.certificate', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'certificate',
    Cell: CertificateCell,
    width: 560,
  };
  const certificateType: LevelTableColumn<
    AccreditationsColumnDefinition,
    AccreditationTableDefinition
  > = {
    Header: () => (
      <Header>
        {t('certificates.table_headers.certificate_type', { ns: 'hq-screen-specific' })}
      </Header>
    ),
    accessor: 'type',
    Cell: CertificateTypeCell,
    width: 140,
  };
  const createdAt: LevelTableColumn<AccreditationsColumnDefinition, AccreditationTableDefinition> =
    {
      Header: (
        cell: LevelTableHeaderProps<AccreditationsColumnDefinition, AccreditationTableDefinition>
      ) => SortableDateHeaderCell(cell, t),
      accessor: 'createdAt',
      Cell: DateCell,
      width: 220,
    };

  const startDate: LevelTableColumn<AccreditationsColumnDefinition, AccreditationTableDefinition> =
    {
      Header: (
        cell: LevelTableHeaderProps<AccreditationsColumnDefinition, AccreditationTableDefinition>
      ) => SortableDateHeaderCell(cell, t),
      accessor: 'startDate',
      Cell: DateCell,
      width: 220,
    };

  const endDate: LevelTableColumn<AccreditationsColumnDefinition, AccreditationTableDefinition> = {
    Header: (
      cell: LevelTableHeaderProps<AccreditationsColumnDefinition, AccreditationTableDefinition>
    ) => SortableDateHeaderCell(cell, t),
    accessor: 'expirationDate',
    Cell: DateCell,
    width: 220,
  };

  const state: LevelTableColumn<AccreditationsColumnDefinition, AccreditationTableDefinition> = {
    Header: () => (
      <Header>{t('certificates.table_headers.status', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'state',
    Cell: StateCell,
    width: 120,
  };

  const isThereADocument: LevelTableColumn<
    AccreditationsColumnDefinition,
    AccreditationTableDefinition
  > = {
    Header: () => (
      <Header>{t('certificates.table_headers.document', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'isThereADocument',
    Cell: IsThereADocumentCell,
    width: 120,
  };

  const actions: LevelTableColumn<AccreditationsColumnDefinition, AccreditationTableDefinition> = {
    Header: () => (
      <Header>{t('certificates.table_headers.action', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'actions',
    Cell: ActionCell,
    width: 80,
  };

  return [
    flexWorkerName,
    certificate,
    certificateType,
    state,
    isThereADocument,
    startDate,
    endDate,
    createdAt,
    actions,
  ];
};

const Header = ({ children }: React.PropsWithChildren<unknown>) => (
  <Typography variant="body2" color="secondary" style={{ fontWeight: 'bold' }}>
    {children}
  </Typography>
);

const SortableDateHeaderCell = (
  cell: LevelTableHeaderProps<AccreditationsColumnDefinition, AccreditationTableDefinition>,
  t: TFunctionNonStrict<'hq-screen-specific', undefined>
) => {
  if (!cell.data[0]) {
    return null;
  }

  let headerText = '';

  switch (cell.column.id) {
    case 'startDate':
      headerText = t('certificates.table_headers.start_date', { ns: 'hq-screen-specific' });
      break;
    case 'expirationDate':
      headerText = t('certificates.table_headers.end_date', { ns: 'hq-screen-specific' });
      break;
    case 'createdAt':
      headerText = t('certificates.table_headers.date_of_addition', { ns: 'hq-screen-specific' });
      break;
    default:
      // eslint-disable-next-line no-console
      console.warn('Unknown column id in SortableDateHeaderCell', cell.column.id);
  }

  const SelectedComponent =
    cell.orderBy === cell.column.id ? StyledSortIconSelected : StyledSortIcon;

  const transformData =
    cell.orderBy === cell.column.id && cell.sortState === 'asc' ? 'rotate(180deg)' : '';

  return (
    <Button
      variant="text"
      onClick={() => {
        cell.onSortClick(cell);
      }}
    >
      <Header>{headerText}</Header>
      <SelectedComponent
        style={{
          transform: transformData,
        }}
      />
    </Button>
  );
};

const FlexWorkerNameCell = (cell: AccreditationCellProp<'flexWorkerName'>) => {
  return (
    <Box display="flex" alignItems="center">
      {cell.value.hasNotification ? (
        <RedDotNotification data-testid="accreditation-notification" />
      ) : null}
      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
        {cell.value.name}
      </Typography>
    </Box>
  );
};

const CertificateCell = (cell: AccreditationCellProp<'certificate'>) => {
  return <Typography variant="subtitle2">{cell.value}</Typography>;
};

const CertificateTypeCell = (cell: AccreditationCellProp<'type'>) => {
  return <Typography variant="subtitle2">{cell.value}</Typography>;
};

const StateCell = (cell: AccreditationCellProp<'state'>) => {
  return <Typography variant="subtitle2">{translateState(cell.value)}</Typography>;
};

const DateCell = (
  cell:
    | AccreditationCellProp<'createdAt'>
    | AccreditationCellProp<'startDate'>
    | AccreditationCellProp<'expirationDate'>
) => {
  if (!cell.value) {
    return null;
  }
  const date = DateTime.fromISO(cell.value, { zone: 'utc' }).setLocale('nl');
  return (
    <Typography variant="subtitle2" style={{ marginLeft: '32px' }}>
      {date.toFormat('ccc, dd-LL-yy')}
    </Typography>
  );
};

const ActionCell = (cell: AccreditationCellProp<'actions'>) => {
  const actions = cell.getRowActions(
    cell.value.flexWorkerId,
    cell.value.certificateId,
    cell.row.original.type
  );

  if (actions.length === 0) {
    return null;
  }

  return (
    <ContextMenuButton menuId={`accreditation-actions-menu-${cell.value.certificateId}`}>
      {actions}
    </ContextMenuButton>
  );
};

const IsThereADocumentCell = (cell: AccreditationCellProp<'isThereADocument'>) => {
  return (
    <Typography variant="subtitle2" style={{ marginLeft: '32px' }}>
      {cell.value ? 'Ja' : 'Nee'}
    </Typography>
  );
};

const StyledSortIcon = styled(SortIcon)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  height: '12px',
  width: '12px',
  transition: 'ease transform .5s',
}));

const StyledSortIconSelected = styled(SortIcon)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  transition: 'ease transform .5s',
  height: '16px',
  width: '16px',
  padding: '1px',
  border: `1px solid ${theme.palette.lwBlack[60]}`,
  borderRadius: '50%',
  boxSizing: 'border-box',
}));

const RedDotNotification = styled(Box)(({ theme }) => ({
  width: theme.spacing(2),
  height: theme.spacing(2),
  borderRadius: '50%',
  backgroundColor: theme.palette.lwDanger[100],
  marginBottom: theme.spacing(8),
  marginRight: theme.spacing(1),
}));
